// js/render/Sprites.js
// 程式化像素精靈：以 16×16 網格構造，烘焙成離屏畫布並快取。
// 零素材檔；drawSprite 以整數像素貼上，放大後即為顆粒像素。

import { PALETTE, charToIndex } from './Palette.js';

const S = 16; // 精靈邊長（像素），剛好 = 1 格

function newCanvas() {
  const c = document.createElement('canvas');
  c.width = S; c.height = S;
  return c;
}
function makeGrid() { return Array.from({ length: S }, () => new Array(S).fill(0)); }
function plot(g, x, y, v) { x |= 0; y |= 0; if (x >= 0 && x < S && y >= 0 && y < S) g[y][x] = v; }
function rect(g, x, y, w, h, v) { for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) plot(g, x + i, y + j, v); }
function hline(g, x, y, w, v) { rect(g, x, y, w, 1, v); }
function vline(g, x, y, h, v) { rect(g, x, y, 1, h, v); }
function line(g, x0, y0, x1, y1, v) {
  x0 |= 0; y0 |= 0; x1 |= 0; y1 |= 0;
  const dx = Math.abs(x1 - x0), sx = x0 < x1 ? 1 : -1;
  const dy = -Math.abs(y1 - y0), sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    plot(g, x0, y0, v);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
function gridToCanvas(g) {
  const c = newCanvas();
  const ctx = c.getContext('2d');
  for (let y = 0; y < S; y++) {
    for (let x = 0; x < S; x++) {
      const idx = g[y][x];
      if (!idx) continue;
      ctx.fillStyle = PALETTE[idx];
      ctx.fillRect(x, y, 1, 1);
    }
  }
  return c;
}

// 由 ASCII 字串陣列構造（輔助用）
export function fromRows(rows) {
  const g = makeGrid();
  for (let y = 0; y < S && y < rows.length; y++) {
    const row = rows[y];
    for (let x = 0; x < S && x < row.length; x++) {
      const idx = charToIndex(row[x]);
      if (idx) g[y][x] = idx;
    }
  }
  return gridToCanvas(g);
}

// 俯視黎先生：圓形人頭（亞洲阿叔）— 禿頭＋兩側剩髮＋白色反光眼鏡。朝上（-y）。
function buildWalker() {
  const g = makeGrid();
  const cx = 7.5, cy = 7.5;
  // 頭（膚色圓＋深色輪廓）
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const d = Math.hypot(x - cx, y - cy);
      if (d <= 6.6) plot(g, x, y, d > 5.6 ? 1 : 16);
    }
  }
  // 地中海剩髮：後方與兩側一圈深髮
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const d = Math.hypot(x - cx, y - cy);
      if (d <= 5.6 && y - cy > 1.2 && d > 3.2) plot(g, x, y, 2);
    }
  }
  // 白色反光眼鏡（朝向前方）
  rect(g, 3, 4, 4, 4, 1);   // 左鏡框
  rect(g, 4, 5, 2, 2, 19);  // 左鏡片（白反光）
  rect(g, 9, 4, 4, 4, 1);   // 右鏡框
  rect(g, 10, 5, 2, 2, 19); // 右鏡片（白反光）
  rect(g, 7, 5, 2, 1, 1);   // 鼻樑
  // 鼻
  plot(g, 7, 7, 8); plot(g, 8, 7, 8);
  // 一臉嚴肅的嘴
  hline(g, 7, 9, 2, 1);
  // 禿頭光澤
  hline(g, 7, 2, 2, 15);
  return gridToCanvas(g);
}

// 電腦（目的地＝公司座位）
function buildComputer() {
  const g = makeGrid();
  // 顯示器外框
  rect(g, 2, 2, 12, 9, 1);
  // 機身
  rect(g, 3, 3, 10, 7, 2);
  // 螢幕（青）
  rect(g, 4, 4, 8, 5, 18);
  // 螢幕內容：文件行（白）＋游標（黃）
  hline(g, 5, 5, 3, 19);
  hline(g, 5, 6, 5, 19);
  hline(g, 5, 7, 4, 19);
  plot(g, 10, 7, 17);
  // 支架與底座
  vline(g, 7, 11, 3, 2); vline(g, 8, 11, 3, 2);
  hline(g, 4, 14, 8, 1);
  return gridToCanvas(g);
}

// 咖啡杯（收集物）
function buildCoffee() {
  const g = makeGrid();
  // 杯身
  rect(g, 4, 5, 7, 8, 8);
  // 杯身輪廓
  hline(g, 4, 5, 7, 1); hline(g, 4, 12, 7, 1);
  vline(g, 4, 5, 8, 1); vline(g, 10, 5, 8, 1);
  // 杯把
  vline(g, 10, 6, 4, 1); vline(g, 12, 6, 4, 1);
  hline(g, 10, 6, 3, 1); hline(g, 10, 9, 3, 1);
  // 咖啡（深色）
  hline(g, 5, 6, 5, 1);
  // 蒸汽
  plot(g, 6, 3, 19); plot(g, 8, 3, 19);
  plot(g, 7, 2, 19);
  // 杯底
  hline(g, 5, 12, 5, 1);
  return gridToCanvas(g);
}

// 凍檸茶（補給：回體力）
function buildLemonTea() {
  const g = makeGrid();
  // 杯（玻璃輪廓）
  rect(g, 4, 4, 8, 9, 1);
  // 茶色液體（金黃）
  rect(g, 5, 6, 6, 6, 17);
  // 冰塊（白）
  plot(g, 6, 7, 19); plot(g, 9, 8, 19);
  // 吸管（紅）
  vline(g, 10, 1, 5, 13);
  // 檸檬片（杯口橘）
  hline(g, 5, 3, 6, 14);
  // 杯底
  hline(g, 4, 13, 8, 1);
  return gridToCanvas(g);
}

// 病菌（危險物，扣體力）：綠色菌團＋深綠輪廓＋鞭毛＋邪惡眼口
function buildGerm() {
  const g = makeGrid();
  const cx = 7.5, cy = 8;
  // 菌身（綠色團＋深綠輪廓）
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const d = Math.hypot(x - cx, y - cy);
      if (d <= 5.6) plot(g, x, y, d > 4.8 ? 12 : 11);
    }
  }
  // 鞭毛（八方向短毛）
  for (let a = 0; a < 8; a++) {
    const ang = (a * Math.PI) / 4;
    line(g, cx + Math.cos(ang) * 5.5, cy + Math.sin(ang) * 5.5,
            cx + Math.cos(ang) * 7.4, cy + Math.sin(ang) * 7.4, 12);
  }
  // 菌斑
  plot(g, 4, 5, 12); plot(g, 11, 6, 12); plot(g, 5, 11, 12);
  plot(g, 10, 10, 12); plot(g, 7, 4, 12);
  // 眼（白＋黑瞳，鬥雞眼）
  rect(g, 5, 7, 2, 2, 19); plot(g, 6, 7, 1);
  rect(g, 9, 7, 2, 2, 19); plot(g, 9, 7, 1);
  // 邪笑
  plot(g, 6, 11, 1); plot(g, 7, 12, 1); plot(g, 8, 12, 1); plot(g, 9, 11, 1);
  return gridToCanvas(g);
}

const cache = {};
const builders = { walker: buildWalker, computer: buildComputer, coffee: buildCoffee, lemonTea: buildLemonTea, germ: buildGerm };

export const Sprites = new Proxy({}, {
  get(_, name) {
    if (!builders[name]) return undefined;
    if (!cache[name]) cache[name] = builders[name]();
    return cache[name];
  },
});
